export const LOCALES = ['en', 'es'] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export const LOCALE_PREFIX: Record<Locale, string> = {
  en: '/en',
  es: '/es',
};

export function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'es';
}

function ensureTrailingSlash(pathname: string): string {
  if (pathname === '/') {
    return pathname;
  }

  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

const LOCALE_PATH_PREFIX = /^\/(en|es)(?=\/|$)/;

function localePrefixMatch(pathname: string) {
  return LOCALE_PATH_PREFIX.exec(pathname);
}

export function stripLocalePrefix(pathname: string): string {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const match = localePrefixMatch(normalized);

  if (!match) {
    return normalized;
  }

  const rest = normalized.slice(match[0].length);
  if (!rest || rest === '/') {
    return '/';
  }

  return rest.startsWith('/') ? rest : `/${rest}`;
}

export function localizePath(pathname: string, locale: Locale): string {
  const canonical = ensureTrailingSlash(stripLocalePrefix(pathname));

  if (canonical === '/') {
    return `${LOCALE_PREFIX[locale]}/`;
  }

  return `${LOCALE_PREFIX[locale]}${canonical}`;
}

export function localeFromPath(pathname: string): Locale {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const match = localePrefixMatch(normalized);

  if (match && isLocale(match[1])) {
    return match[1];
  }

  return DEFAULT_LOCALE;
}
